// SPDX-License-Identifier: GPL-3.0-or-later
// Code-built trigger (rollover lane switch). The upstream trigger loads JSON meshes.
// A wire shape is a low ring. A star is a flat star. A button is a low cylinder.
// The origin is the centre on the playfield.

import { type BufferGeometry, CylinderGeometry, TorusGeometry } from "three";
import type { TriggerSpec } from "../../shared/contracts.ts";
import { INSERT_LIFT } from "../coords.ts";
import { extrudeOutline, type Point2 } from "./shared.ts";

/** Radius of the trigger: the spec radius, else 20. */
export function triggerRadius(spec: TriggerSpec): number {
  return spec.radius ?? 20;
}

/** A five-point star outline. */
export function starOutline(outer: number, inner: number): Point2[] {
  const out: Point2[] = [];
  for (let i = 0; i < 10; i++) {
    const r = i % 2 === 0 ? outer : inner;
    const a = (i / 10) * Math.PI * 2 - Math.PI / 2;
    out.push({ x: Math.cos(a) * r, y: Math.sin(a) * r });
  }
  return out;
}

/** Build the geometry of a trigger, or null when the shape is invisible. */
export function generateTrigger(spec: TriggerSpec): BufferGeometry | null {
  const shape = spec.shape ?? 0;
  const r = triggerRadius(spec);
  const wire = Math.max(spec.wireThickness ?? 3, 1);
  let geometry: BufferGeometry;
  switch (shape) {
    case 0:
      return null;
    case 2:
      geometry = extrudeOutline(starOutline(r * 0.8, r * 0.35), 2);
      break;
    case 4:
      geometry = new CylinderGeometry(r * 0.5, r * 0.55, 4, 24);
      geometry.translate(0, 2, 0);
      break;
    default: {
      // Wire shapes 1, 3, 5 and 6 differ in the ratio of the two radii of the ring.
      const ratio =
        shape === 3 ? [0.55, 1.2] : shape === 5 ? [1.2, 0.55] : shape === 6 ? [0.8, 0.8] : [1, 1];
      geometry = new TorusGeometry(r * 0.7, wire, 8, 28);
      geometry.rotateX(Math.PI / 2);
      geometry.scale(ratio[0] as number, 1, ratio[1] as number);
      geometry.translate(0, wire, 0);
    }
  }
  geometry.translate(0, INSERT_LIFT, 0);
  geometry.name = `trigger-${spec.name}`;
  return geometry;
}
